import { createDateRangeFromPreset, type DateRange } from "@/hooks/useFilters";
import { useEffect, useRef, useState } from "react";

interface DateRangeFilterProps {
  /** Current date range value */
  value: DateRange;
  /** Callback when value changes */
  onChange: (value: DateRange) => void;
  /** Callback when filter is removed */
  onRemove: () => void;
  /** Whether to open immediately */
  initialOpen?: boolean;
}

type PresetOption = "today" | "last7days" | "last30days" | "custom";

const PRESETS: { value: PresetOption; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "custom", label: "Custom Range" },
];

/**
 * Format a date for input[type="date"]
 */
function formatDateForInput(date: Date | null): string {
  if (!date) return "";
  return date.toISOString().split("T")[0];
}

/**
 * Format a date range for display
 */
function formatDateRangeDisplay(dateRange: DateRange): string {
  if (dateRange.preset && dateRange.preset !== "custom") {
    const preset = PRESETS.find((p) => p.value === dateRange.preset);
    return preset?.label || "All";
  }

  if (!dateRange.startDate && !dateRange.endDate) {
    return "All";
  }

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  if (dateRange.startDate && dateRange.endDate) {
    return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
  }
  if (dateRange.startDate) {
    return `From ${formatDate(dateRange.startDate)}`;
  }
  if (dateRange.endDate) {
    return `Until ${formatDate(dateRange.endDate)}`;
  }

  return "All";
}

/**
 * DateRangeFilter component - Date picker with preset options
 *
 * Presets:
 * - Today
 * - Last 7 Days
 * - Last 30 Days
 * - Custom Range (shows date pickers)
 */
export function DateRangeFilter({
  value,
  onChange,
  onRemove,
  initialOpen = false,
}: DateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [selectedPreset, setSelectedPreset] = useState<PresetOption>(value.preset || "custom");
  const [startDate, setStartDate] = useState<string>(formatDateForInput(value.startDate));
  const [endDate, setEndDate] = useState<string>(formatDateForInput(value.endDate));
  const containerRef = useRef<HTMLDivElement>(null);

  // React to initialOpen prop changes (for auto-open on add)
  useEffect(() => {
    if (initialOpen) {
      setIsOpen(true);
    }
  }, [initialOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handlePresetChange = (preset: PresetOption) => {
    setSelectedPreset(preset);

    if (preset !== "custom") {
      const dateRange = createDateRangeFromPreset(preset);
      setStartDate(formatDateForInput(dateRange.startDate));
      setEndDate(formatDateForInput(dateRange.endDate));
      onChange(dateRange);
      setIsOpen(false);
    }
  };

  const handleCustomDateChange = () => {
    const newRange: DateRange = {
      startDate: startDate ? new Date(startDate + "T00:00:00") : null,
      endDate: endDate ? new Date(endDate + "T23:59:59") : null,
      preset: "custom",
    };
    onChange(newRange);
  };

  const displayValue = formatDateRangeDisplay(value);

  return (
    <div ref={containerRef} className="relative">
      {/* Chip button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-1.5 pr-7
          text-xs
          ${isOpen ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-[#232f48]"}
          border border-gray-200 dark:border-[#324467]
          rounded-full
          hover:bg-gray-50 dark:hover:bg-gray-700
          transition-colors duration-150
        `}
      >
        <span className="material-symbols-outlined text-sm text-gray-500">calendar_today</span>
        <span className="text-gray-600 dark:text-gray-300">{displayValue}</span>
      </button>

      {/* Remove button */}
      <button
        type="button"
        aria-label="Remove date filter"
        title="Remove date filter"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={`
          absolute right-1.5 top-1/2 -translate-y-1/2 p-0
          text-gray-400 hover:text-gray-600
          dark:text-gray-500 dark:hover:text-gray-300
          transition-colors duration-150
        `}
      >
        <span className="material-symbols-outlined text-sm">close</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`
            absolute left-0 top-full mt-1 z-50
            w-72 p-3
            bg-white dark:bg-[#192233]
            border border-gray-200 dark:border-[#324467]
            rounded-lg shadow-lg
          `}
        >
          {/* Preset options */}
          <div className="space-y-1 mb-3">
            {PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePresetChange(preset.value)}
                className={`
                  w-full px-3 py-2 text-left text-sm rounded-md
                  transition-colors duration-150
                  ${
                    selectedPreset === preset.value
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }
                `}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom date range inputs */}
          {selectedPreset === "custom" && (
            <div className="border-t border-gray-200 dark:border-[#324467] pt-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onBlur={handleCustomDateChange}
                  className={`
                    w-full px-3 py-2
                    text-sm text-gray-900 dark:text-white
                    bg-white dark:bg-[#111722]
                    border border-gray-300 dark:border-[#324467]
                    rounded-md
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                  `}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onBlur={handleCustomDateChange}
                  className={`
                    w-full px-3 py-2
                    text-sm text-gray-900 dark:text-white
                    bg-white dark:bg-[#111722]
                    border border-gray-300 dark:border-[#324467]
                    rounded-md
                    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
                  `}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  handleCustomDateChange();
                  setIsOpen(false);
                }}
                className={`
                  w-full py-2
                  text-sm font-medium text-white
                  bg-primary hover:bg-primary/90
                  rounded-md
                  transition-colors duration-150
                `}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
